import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { IROH_SERVICES_API_PREFIX } from "@/api/domain"
import { connectWs } from "@/api/ws"
import type { CloudProvider, RegionName } from "@/infra"
import type { ProjectIdParam, Uuid } from "@/types"
import { ExecStatus, type Workflow, type WorkflowUpdate } from "@/workflows"

type CreateWorkflowParams = ProjectIdParam & {
  region: RegionName
  name: string
  type: string
  build_id?: Uuid // TODO make this mandatory
  cloud_provider?: CloudProvider
}

type UpdateWorkflowParams = ProjectIdParam & {
  instance_id: Uuid
  deploy_id: Uuid
  build_id?: Uuid // TODO make this mandatory
}

type DeleteWorkflowParams = ProjectIdParam & {
  deploy_id: Uuid
}

type WorkflowGetParams = ProjectIdParam & {
  workflow_id: Uuid
}

type WorkflowGetWsParams = ProjectIdParam & {
  // This callback is passed into the websocket message processing event handler
  // to capture state transitions between workflows. Don't modify the prev & next
  // values passed in here: they're raw values from the state tree
  onChange?: (prev: Workflow, next: Workflow) => void
}

// Define a service using a base URL and expected endpoints
export const workflowsApi = createApi({
  reducerPath: "workflows",
  baseQuery: fetchBaseQuery({
    baseUrl: `${IROH_SERVICES_API_PREFIX}workflows`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    relayCreatePost: build.mutation<void, CreateWorkflowParams>({
      query: (body) => ({
        url: `/${body.project_id}/create-relay`,
        method: "POST",
        body,
      }),
    }),
    dnsCreatePost: build.mutation<void, CreateWorkflowParams>({
      query: (body) => ({
        url: `/${body.project_id}/create-dns`,
        method: "POST",
        body,
      }),
    }),
    workflowUpdatePost: build.mutation<void, UpdateWorkflowParams>({
      query: (body) => ({
        url: `/${body.project_id}/update`,
        method: "POST",
        body,
      }),
    }),
    workflowDeletePost: build.mutation<void, DeleteWorkflowParams>({
      query: (body) => ({
        url: `/${body.project_id}/delete`,
        method: "POST",
        body,
      }),
    }),

    workflowsGetWs: build.query<Record<Uuid, Workflow>, WorkflowGetWsParams>({
      query: ({ project_id }) => ({
        url: project_id,
        params: { limit: 100, offset: 0 },
      }),
      transformResponse: (baseQueryReturnValue, _meta, _arg) => {
        const mapped: Record<Uuid, Workflow> = {}
        const workflows = baseQueryReturnValue as Workflow[]
        for (const wf of workflows) {
          mapped[wf.id] = wf
        }
        return mapped
      },

      async onCacheEntryAdded(
        args,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded

        const cleanup = connectWs(
          `${IROH_SERVICES_API_PREFIX}workflows/${args.project_id}/ws/`,
          (event: MessageEvent) => {
            const wf: Workflow = JSON.parse(event.data)
            updateCachedData((draft) => {
              const prev = draft[wf.id]
              if (args.onChange && prev) {
                args.onChange(prev, wf)
              }
              draft[wf.id] = wf
            })
          },
        )
        await cacheEntryRemoved
        cleanup()
      },
    }),

    workflowGet: build.query<Workflow, WorkflowGetParams>({
      query: ({ project_id, workflow_id }) => `${project_id}/${workflow_id}`,
    }),

    workflowGetWs: build.query<Workflow, WorkflowGetParams>({
      query: ({ project_id, workflow_id }) => `${project_id}/${workflow_id}`,

      async onCacheEntryAdded(
        args,
        { cacheDataLoaded, cacheEntryRemoved, updateCachedData },
      ) {
        const workflowRes = await cacheDataLoaded

        if (
          !(
            workflowRes.data?.status === ExecStatus.Pending ||
            workflowRes.data?.status === ExecStatus.Running
          )
        ) {
          return
        }

        const cleanup = connectWs(
          `${IROH_SERVICES_API_PREFIX}workflows/${args.project_id}/${args.workflow_id}/ws/`,
          (event: MessageEvent) => {
            const update: WorkflowUpdate = JSON.parse(event.data)
            updateCachedData((draft) => {
              if (update.msg_type === "action_log") {
                const action = draft.actions?.find(
                  (a) => a.id === update.action_id,
                )
                if (action) {
                  action.log = action.log + update.message
                }
              } else if (update.msg_type === "action_status_change") {
                const action = draft.actions?.find((a) => a.id === update.id)
                if (action) {
                  action.status = update.status
                }
              } else if (update.msg_type === "exec_status") {
                draft.status = update.status
              }
            })
          },
        )
        await cacheEntryRemoved
        cleanup()
      },
    }),
  }),
})

export const {
  useRelayCreatePostMutation,
  useDnsCreatePostMutation,
  useWorkflowUpdatePostMutation,
  useWorkflowDeletePostMutation,
  useWorkflowsGetWsQuery,
  useWorkflowGetWsQuery,
} = workflowsApi
